import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';

import {
  GoalData,
  Goal,
} from '../../../../@core/interfaces/common/CalorieFriend/goals';
import { Utility } from '../../../../@core/utils/utility';
import { BaseComponentCanDeactivate } from 'app/@auth/guards/changes.guard';
import {
  Patient,
  PatientData,
} from 'app/@core/interfaces/common/CalorieFriend/patients';

/**
 * Component for define a new goal.
 * @usageNotes
 * It is used to add a new goal on the Patient Sheet View.
 * It can be opened via NgDialogService as the content of the pop up dialog
 * or can be shown as a standalone page directly.
 */
@Component({
  selector: 'app-goal-def',
  templateUrl: './goal-def.component.html',
  styleUrls: ['./goal-def.component.scss', '../../global.component.scss'],
})
export class GoalDefComponent
  extends BaseComponentCanDeactivate
  implements OnInit {
  GoalLabel = '';
  NewGoalLabel = 'New Goal';
  EditGoalLabel = 'Edit Goal';

  GoalNameLabel = 'Goal Name';
  GoalTypeLabel = 'Goal Type';
  GoalValueLabel = 'Goal Value';
  ProgressLabel = 'Progress';

  SaveGoalLabel = 'Save';
  BackLabel = 'Back';
  Created = 'Created';
  Updated = 'Updated';
  ProblemSavingGoal = 'Problem occured saving the Goal';
  GoalDateLabel = 'Target Date';
  DisableDate = '0';

  GoalForm: FormGroup;

  PatientID = null;
  ID = null;
  Progress = 0;

  Patient: Patient = null;

  protected readonly unsubscribe$ = new Subject<void>();

  /** GoalDef ctor */
  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private dialogRef: NbDialogRef<GoalDefComponent>,
    private location: Location,
    private goalService: GoalData,
    private patientService: PatientData,
    private toasterService: NbToastrService,
    dialogService: NbDialogService,
  ) {
    super(dialogService);

    this.PatientID = +this.route.snapshot.paramMap.get('PatientId');
    this.ID = +this.route.snapshot.paramMap.get('ID');

    this.InitUserForm();
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.InitUserForm();

    this.loadPatientData();

    if (this.ID === null || this.ID === 0) {
      this.GoalLabel = this.NewGoalLabel;
      this.DisableDate = '0';
    } else {
      this.GoalLabel = this.EditGoalLabel;
      this.loadGoalData(this.ID);
      this.DisableDate = '0';
    }
  }

  get GoalName() {
    return this.GoalForm.get('GoalName');
  }
  get GoalType() {
    return this.GoalForm.get('GoalType');
  }
  get GoalValue() {
    return this.GoalForm.get('GoalValue');
  }
  get targetDate() {
    return this.GoalForm.get('targetDate');
  }

  GoalValueMax: number = 1000;

  /**
   * Initializes the form for Goal edition
   */
  InitUserForm() {
    const goalDate = new Date();
    goalDate.setHours(0, 0, 0, 0);
    goalDate.setDate(goalDate.getDate() + 30);

    this.GoalForm = this.fb.group({
      GoalID: this.fb.control(''),
      PatientID: this.fb.control(''),
      ID: this.fb.control(''),
      targetDate: this.fb.control(goalDate, [Validators.required]),
      GoalName: this.fb.control('', [
        Validators.required,
        Validators.minLength(1),
        Validators.max(200),
      ]),
      GoalType: this.fb.control('', [Validators.required]),
      GoalValue: this.fb.control('', [
        Validators.required,
        Validators.min(0),
        Validators.max(1000),
      ]),
    });

    this.GoalForm.get('GoalType').valueChanges.subscribe((value) => {
      if (+value === 0) {
        this.GoalValueMax = 100;
      } else {
        this.GoalValueMax = 1000;
      }
      this.GoalForm.get('GoalValue').setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(this.GoalValueMax),
      ]);
      this.GoalForm.get('GoalValue').updateValueAndValidity();
    });

    super.initForm(this.GoalForm);
  }

  /**
   * It closes the dialog if it is opened on it.
   * If not, it goes back to the previous page.
   */
  Back() {
    // this.router.navigate(['/pages/CalorieFriend/Goals/' + this.PatientID]);
    super.back(() => {
      if (this.dialogRef) this.dialogRef.close();
      else this.location.back();
    });
  }

  /**
   * Converts the object given as a parameter to the 'Goal' type
   * @param value Object to convert into Goal type
   */
  ConvertToGoal(value: any): Goal {
    const goal: Goal = value;
    return goal;
  }

  /**
   * It converts the inputs of the form into the Goal type
   * and submit the form to save them
   */
  SaveGoal() {
    const goal: any = this.ConvertToGoal(this.GoalForm.value);

    goal.id = this.ID;
    goal.PatientID = this.PatientID;

    const targetDateUtc = goal.targetDate;
    goal.targetDate = new Date(
      targetDateUtc.getTime() - targetDateUtc.getTimezoneOffset() * 60000,
    );

    if (this.Patient.isPreferredUnitImperial && goal.GoalType === 2) {
      goal.GoalValue = goal.GoalValue * 0.453592;
    }

    const observable =
      goal.id === null || goal.id === 0
        ? this.goalService.create(goal)
        : this.goalService.update(goal);

    this.resetDirty();

    observable.pipe(takeUntil(this.unsubscribe$)).subscribe(
      () => {
        this.handleSuccessResponse();
      },
      (err) => {
        this.handleWrongResponse();
      },
    );
  }

  /**
   * Handler of Success event of submit
   */
  handleSuccessResponse() {
    if (this.ID === null)
      this.toasterService.success('', `Item ${this.Created}!`);
    else this.toasterService.success('', `Item ${this.Updated}!`);

    this.Back();
  }

  /**
   * Handler of Fail event of submit
   */
  handleWrongResponse() {
    this.toasterService.danger('', this.ProblemSavingGoal);
  }

  /**
   * Loads the data of the Goal given by {id}
   * @param id The identifier of the Goal
   */
  loadGoalData(id) {
    if (id) {
      this.goalService
        .get(id)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((goal) => {
          this.Progress = goal.progress;

          let goalValue = goal.goalValue;
          if (this.Patient.isPreferredUnitImperial && goal.goalType === 2) {
            goalValue = Math.round((goalValue / 0.453592) * 10) / 10;
          } else {
            goalValue = Math.round(goalValue * 10) / 10;
          }

          this.GoalForm.patchValue({
            ID: this.ID,
            PatientID: this.PatientID,
            targetDate: new Date(goal.targetDate),
            GoalName: goal.goalName,
            GoalType: goal.goalType,
            GoalValue: goalValue,
          });

          super.resetDirty();
        });
    }
  }

  /**
   * Loads the data of the Patient given by {PatientID}
   * @param id The identifier of the Goal
   */
  loadPatientData() {
    if (this.PatientID) {
      this.patientService
        .get(this.PatientID, null)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((p) => {
          this.Patient = p;
        });
    }
  }

  /**
   * Validator that validates the number with the ##.# pattern
   */
  validateNumber = Utility.validateNumber;
}
